import { IconChevronDown } from '@developerz/ui';
import { createMemo, createUniqueId, For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { pressMove, useCardMove } from '../../lib/card-move';
import type { UtilBox } from '../../lib/fleetUtilization';
import type { JobGroup as JobGroupView } from '../../lib/machine-jobs';
import { groupModeKey, groupSubject } from '../../lib/move-items';
import { JobRow } from './JobRow';
import { MoveButton } from './MoveButton';
import { MoveGrip } from './MoveGrip';
import { MoveToMenu } from './MoveToMenu';

// One capability group of a machine's jobs (#6745, prototype jobsHTML, `:2213-2222`): a
// dot (the accent with the slow pulse while any job in it is working or starting), the
// capability, "N jobs" and the summed usage, as a button with `aria-expanded` that folds
// the list.
//
// THE GROUP MOVE (#6771): the header carries a grip and a Move button only when every job under
// it shares ONE non-null `mode_key` (`groupModeKey`): the header groups by role, and a babysit
// round reads Coder while riding the CI Fixer switch, so a mixed header gets none and its jobs
// move one by one. A group move names the shared mode key.

export function JobGroup(props: {
  readonly group: JobGroupView;
  readonly box: UtilBox;
  readonly nowMs: number;
  readonly closed: boolean;
  readonly onToggle: () => void;
  readonly isOpen: (jobId: string) => boolean;
  readonly onToggleJob: (jobId: string) => void;
}): JSX.Element {
  const listId = createUniqueId();
  const move = useCardMove();
  const subject = createMemo(() => {
    if (move === null || !move.on()) return null;
    const key = groupModeKey(props.box, props.group.jobs, move.byTask());
    return key === null ? null : groupSubject(props.box, key, props.group.jobs, t);
  });
  return (
    <div class="mission-jobs__group" data-closed={props.closed ? '' : undefined}>
      <div
        class="mission-jobs__group-bar"
        data-move-row={subject()?.key}
        onPointerDown={(e) => {
          const s = subject();
          if (move !== null && s !== null) pressMove(move, s, e);
        }}
      >
        <Show when={subject()}>{(s) => <MoveGrip subject={s()} />}</Show>
        <button
          type="button"
          class="mission-jobs__group-head"
          aria-expanded={!props.closed}
          aria-controls={listId}
          onClick={() => props.onToggle()}
        >
          <IconChevronDown class="mission-jobs__chev" size="0.75rem" />
          <span
            class="mission-jobs__dot"
            data-live={props.group.live ? '' : undefined}
            aria-hidden="true"
          />
          {/* The spaces keep the button's accessible name readable ("Coder 2 jobs CPU 14% …");
            the flex gap lays the spans out, so they are not visible. */}
          <span class="mission-jobs__group-name">{props.group.name}</span>{' '}
          <span class="mission-jobs__group-count">{props.group.count}</span>
          <Show when={props.group.usage}>
            {(u) => (
              <>
                {' '}
                <span class="mission-jobs__group-usage">{u()}</span>
              </>
            )}
          </Show>
        </button>
        <Show when={subject()}>{(s) => <MoveButton subject={s()} />}</Show>
      </div>
      <Show when={subject()}>{(s) => <MoveToMenu subject={s()} />}</Show>
      <Show when={!props.closed}>
        <ul id={listId} class="mission-jobs__list">
          {/* Keyed by job id, looked up in the current read, so a poll keeps each row. */}
          <For each={props.group.jobs.map((j) => j.id)}>
            {(id) => (
              <Show when={props.group.jobs.find((j) => j.id === id)}>
                {(job) => (
                  <JobRow
                    job={job()}
                    box={props.box}
                    nowMs={props.nowMs}
                    open={props.isOpen(id)}
                    onToggle={() => props.onToggleJob(id)}
                  />
                )}
              </Show>
            )}
          </For>
        </ul>
      </Show>
    </div>
  );
}
